import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import TradingChart from './TradingChart';
import CombinedPanel from './CombinedPanel';
import StatusBar from './StatusBar';
import { WebSocketManager } from '../services/websocket';
import { ApiService } from '../services/api';
import { ChartData, TradeMarker, DrawingData, SessionData } from '../types';

interface CandleInfo {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ChartViewState {
  sessionId: string | null;
  sessionData: SessionData | null;
  chartData: ChartData | null;
  trades: TradeMarker[];
  drawings: DrawingData | null;
  connectionStatus: 'connecting' | 'connected' | 'disconnected';
  error: string | null;
  selectedTool: string;
  hoveredCandle: CandleInfo | null;
}

const ChartView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  
  const [state, setState] = useState<ChartViewState>({
    sessionId: null,
    sessionData: null,
    chartData: null,
    trades: [],
    drawings: null,
    connectionStatus: 'connecting',
    error: null,
    selectedTool: 'cursor',
    hoveredCandle: null
  });

  const apiService = new ApiService();
  const [wsManager, setWsManager] = useState<WebSocketManager | null>(null);

  const handleWebSocketMessage = useCallback((data: any) => {
    switch (data.type) {
      case 'chart_data':
        setState(prev => ({
          ...prev,
          chartData: data.data
        }));
        break;
      
      case 'trade_marker':
        setState(prev => ({
          ...prev,
          trades: [data.data, ...prev.trades.slice(0, 99)] // Keep last 100 trades
        }));
        break;
      
      case 'drawings':
        setState(prev => ({
          ...prev,
          drawings: data.data
        }));
        break;
    }
  }, []);

  const handleConnectionStatusChange = useCallback((status: 'connecting' | 'connected' | 'disconnected') => {
    setState(prev => ({ ...prev, connectionStatus: status }));
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState(prev => ({
        ...prev,
        error: 'No session ID provided in URL',
        connectionStatus: 'disconnected'
      }));
      return;
    }

    setState(prev => ({ ...prev, sessionId }));

    // Load initial data
    const loadInitialData = async () => {
      try {
        const history = await apiService.getHistory(sessionId);
        
        setState(prev => ({
          ...prev,
          sessionData: history.session,
          chartData: history.chart,
          trades: history.trades || [],
          drawings: history.drawings,
          connectionStatus: 'connected'
        }));

        // Connect WebSocket
        const ws = new WebSocketManager(
          sessionId,
          handleWebSocketMessage,
          handleConnectionStatusChange
        );
        
        setWsManager(ws);
        ws.connect();

      } catch (error) {
        setState(prev => ({
          ...prev,
          error: `Failed to load session data: ${error}`,
          connectionStatus: 'disconnected'
        }));
      }
    };

    loadInitialData();

    return () => {
      wsManager?.disconnect();
    };
  }, [sessionId, handleWebSocketMessage, handleConnectionStatusChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToolSelect = (tool: string) => {
    setState(prev => ({ ...prev, selectedTool: tool }));
  };

  const handleCandleHover = useCallback((candleInfo: CandleInfo | null) => {
    setState(prev => ({ ...prev, hoveredCandle: candleInfo }));
  }, []);

  // Redirect to dashboard if no session ID
  if (!sessionId) {
    return <Navigate to="/" replace />;
  }

  if (state.error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <h2>Connection Error</h2>
          <p>{state.error}</p>
          <button onClick={() => window.location.reload()}>
            Retry
          </button>
          <button onClick={() => window.location.href = '/'}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!state.sessionData || !state.chartData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading chart data...</p>
      </div>
    );
  }

  return (
    <div className="app">
      <StatusBar 
        sessionData={state.sessionData}
        connectionStatus={state.connectionStatus}
        tradesCount={state.trades.length}
      />
      
      <div className="chart-container">
        <TradingChart
          chartData={state.chartData}
          trades={state.trades}
          drawings={state.drawings}
          selectedTool={state.selectedTool}
          onCandleHover={handleCandleHover}
        />
        
        <CombinedPanel 
          trades={state.trades}
          sessionData={state.sessionData}
          selectedTool={state.selectedTool}
          onToolSelect={handleToolSelect}
          hoveredCandle={state.hoveredCandle}
        />
      </div>
    </div>
  );
};

export default ChartView;